import React, { Component } from 'react';
import { HTTPService } from "./../../../services/httpService";
import { convertFromUTC } from '../../../util/convert_timezone';
import JOI from 'joi-browser';
import "../../../assets/css/SearchResult.css";

import Autocomplete from "react-google-autocomplete";
import { isViewer as isViewerRole } from "../../../util/permissions";
import { isMarineVehicle } from "../../../util/assetType";
import { isAreaPriced, formatMarineLine } from "../../../util/marineService";

require('dotenv').config();

const API = new HTTPService();

export default class Customers extends Component {

    constructor(props) {
        super(props);
        this.searchInputRef = React.createRef();
        this._isMounted = true; // Track component mount status
    }

    state = { 
        data: {
            phone: "",
            email: "",
            name: "",
            type: "",
            id: "",
            input: "",
            stripe_id: "",
            billingEmail: "", // Added for dealership
            // Dealer group fields
            dealerGroupId: null,
            dealerGroupName: null,
            dealerGroupCode: null
        },
        errors: {},
        found: " ",
        value: "",
        suggestions: [],
        customerList: [],
        dealerList: [], // Added for dealership
        customerJobs: [],
        text: "",
        isDealerMode: false, // Toggle between customer and dealer mode
        address: "",
        // Separate address components for dealers
        addressComponents: {
            line1: "",
            line2: "",
            city: "",
            state: "",
            zip: ""
        },
        // Template-related state
        templates: [], // List of available templates
        selectedTemplateId: "", // Currently selected template ID
        currentTemplateId: "", // Template currently assigned to this dealership
        
        // Job detail expansion
        expandedJobRows: [],
        expandedJobServices: {},

        // Enhanced search state
        searchTerm: "",
        filteredResults: [],
        showDropdown: false,
        selectedIndex: -1 // For keyboard navigation
    };

    async componentDidMount() {
        // Isolated on purpose. These were sequential unguarded awaits, so a
        // failure in loadCustomers meant loadDealers NEVER RAN — dealerList
        // stayed empty and Dealer-mode search silently found nothing, for a
        // reason that had nothing to do with dealers. The two lists are
        // independent and one must not be able to take the other down.
        try {
            await this.loadCustomers();
        } catch (error) {
            console.error("[Customers] Failed to load the customer list:", error);
        }
        try {
            await this.loadDealers();
        } catch (error) {
            console.error("[Customers] Failed to load the dealer list:", error);
        }
        // Load available templates - but handle errors gracefully
        try {
            await this.loadTemplates();
        } catch (error) {
            console.error("Failed to load templates on mount:", error);
            // Optionally enable mock data for testing
            // this.setMockTemplates();
        }
    };

    componentWillUnmount() {
        // Mark component as unmounted to prevent state updates
        this._isMounted = false;
    }

    formatAddress = (line1, city, state, zip) => {
        // Filter out empty/null/undefined values and join with appropriate separators
        const parts = [line1, city, state, zip].filter(part => part && part.trim());
        
        if (parts.length === 0) return '';
        
        // Join with commas, but add space before state and zip
        if (parts.length >= 3) {
            const [street, ...rest] = parts;
            return `${street}, ${rest.join(' ')}`;
        }
        
        return parts.join(', ');
    };

    // Load customers from API
    async loadCustomers() {
        const customerRequest = "/customerInstall/" + this.props.typeId;
        const { data } = await API.get(customerRequest);
        // Defensive for the same reasons loadDealers already is: a non-array
        // response threw on .forEach, and a row with a missing name produced the
        // literal string "undefined undefined" in the dropdown.
        const customers = Array.isArray(data) ? data : [];
        customers.forEach(function (element) {
            element.listValue = [element.customer_name, element.customer_phone_number]
                .filter(Boolean).join(' ') || `Customer #${element.customer_id}`;
        });
        this.setState({ customerList: customers });
        //console.log("loadCustomers: Customer List ", customers);
    }

    // Load dealers from API
    async loadDealers() {
        const dealerRequest = "/dealerInstall/" + this.props.typeId;
        const { data } = await API.get(dealerRequest);
        const dealers = Array.isArray(data) ? data : [];
        dealers.forEach(function (element) {
            // Built defensively, and searchable by phone like the customer list.
            // A bare `element.dealer_name` left listValue undefined whenever a
            // dealership had no name — and the filter below called .toLowerCase()
            // on it, which threw and killed the ENTIRE dropdown, not just that row.
            element.listValue = [element.dealer_name, element.dealer_phone_number]
                .filter(Boolean).join(' ') || `Dealership #${element.dealer_id}`;
        });
        this.setState({ dealerList: dealers });
        // Names the endpoint and the count, so an empty picker is immediately
        // distinguishable from a picker whose rows were filtered out.
        console.log(`[Customers] ${dealerRequest} returned ${dealers.length} dealership(s)`);
    }

    // A dealership is inactive when the server flags is_active false/0 (active by default).
    // Safe default: no flag ⇒ treated active, so we never mis-mark a live dealer.
    isDealerInactive = (obj) => !!obj && (obj.is_active === false || obj.is_active === 0 || obj.is_active === '0');

    renderInactiveBadge = () => (
        <span style={{
            marginLeft: '8px', fontSize: '11px', fontWeight: 600, color: '#b91c1c',
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px',
            padding: '1px 6px', textTransform: 'uppercase', letterSpacing: '0.03em',
            verticalAlign: 'middle', whiteSpace: 'nowrap'
        }}>Inactive</span>
    );

    // Load available templates for this specialist/installer
    async loadTemplates() {
        try {
            // Use your existing template endpoint
            const templateRequest = "/templates/" + this.props.typeId;
            //console.log("Attempting to load templates from:", templateRequest);
            
            const response = await API.get(templateRequest);
            //console.log("Templates API response:", response);
            
            // Your API returns { success: true, data: [...], count: number }
            let templates = [];
            if (response.data && response.data.success && response.data.data) {
                templates = response.data.data;
            } else if (response.data && Array.isArray(response.data)) {
                // Fallback if response format is different
                templates = response.data;
            } else if (response && Array.isArray(response)) {
                // Another fallback
                templates = response;
            }
            
            // Ensure templates is always an array
            const templateArray = Array.isArray(templates) ? templates : [];
            this.setState({ templates: templateArray });
            //console.log("loadTemplates: Template List ", templateArray);
            
            if (templateArray.length === 0) {
                //console.log("No templates found for specialist ID:", this.props.typeId);
            }
            
        } catch (error) {
            console.error("Error loading templates:", error);
            console.error("Full error details:", error.response || error);
            
            // For now, set empty array and log that endpoint might not exist
            this.setState({ templates: [] });
            
            // Uncomment this line to use mock data for testing
            // this.setMockTemplates();
        }
    }

    // Mock templates for testing - uncomment this method if you want to test with sample data
    setMockTemplates() {
        const mockTemplates = [
            { template_id: 1, template_name: "Standard Catalog" },
            { template_id: 2, template_name: "Premium Catalog" },
            { template_id: 3, template_name: "Custom Branded Catalog" }
        ];
        this.setState({ templates: mockTemplates });
        //console.log("Using mock templates:", mockTemplates);
    }

    // Load current template assignment for a dealership from customer_templates table
    async loadDealershipTemplate(dealerId) {
        try {
            //console.log("Loading template assignments for dealer:", dealerId, "specialist:", this.props.typeId);
            
            // Try multiple approaches to get the current template assignment
            let currentTemplateId = "";
            
            // Approach 1: Try to get dealerships for each template to find which one has this dealer
            if (this.state.templates && this.state.templates.length > 0) {
                for (const template of this.state.templates) {
                    try {
                        const dealershipsRequest = "/templates/" + this.props.typeId + "/" + template.template_id + "/dealerships";
                        //console.log("Checking template", template.template_id, "for dealer assignments:", dealershipsRequest);
                        
                        const { data: response } = await API.get(dealershipsRequest);
                        //console.log("Dealerships for template", template.template_id, ":", response);
                        
                        if (response && response.success && response.data) {
                            // Check if this dealer is in the list
                            const dealerFound = response.data.find(assignment =>
                                assignment.customer_id === dealerId ||
                                assignment.dealership_id === dealerId ||
                                assignment.dealer_id === dealerId
                            );
                            
                            if (dealerFound) {
                                currentTemplateId = template.template_id;
                                //console.log("Found dealer", dealerId, "assigned to template", template.template_id);
                                break; // Found the assignment, stop looking
                            }
                        }
                    } catch (error) {
                        // If 404, template has no dealerships assigned - that's fine
                        if (error.response && error.response.status !== 404) {
                            //console.log("Error checking template", template.template_id, ":", error);
                        }
                    }
                }
            }
            
            //console.log("Final template assignment result:", currentTemplateId);
            
            if (this._isMounted) {
                this.setState({ 
                    currentTemplateId: currentTemplateId,
                    selectedTemplateId: currentTemplateId
                });
            }
            
        } catch (error) {
            console.error("Error loading dealership template assignments:", error);
            
            // Only update state if component is still mounted - set to empty (no template assigned)
            if (this._isMounted) {
                this.setState({ 
                    currentTemplateId: "",
                    selectedTemplateId: ""
                });
            }
        }
    }

    /**
     * The templates offerable to the dealership currently open.
     *
     * A template's `asset_type` is derived server-side from the services in it,
     * so an auto dealership is never offered a marine template and vice versa.
     *
     * The currently-assigned template is ALWAYS kept, even when it does not
     * match. A dealership can already hold an assignment made before types
     * existed, or before its own type was set — filtering that out would show
     * "No Template Assigned" while the server still had one assigned, which is a
     * silent disagreement rather than a tidy list.
     *
     * Both sides default to 'auto' so this behaves correctly before the columns
     * are populated: everything is auto, nothing is filtered.
     */
    visibleTemplates() {
        const templates = this.state.templates || [];
        const dealerType = this.state.data.assetType || 'auto';
        return templates.filter(t =>
            (t.asset_type || 'auto') === dealerType
            || t.template_id === this.state.currentTemplateId
        );
    }

    // Update template assignment for dealership using existing template system
    async updateDealershipTemplate(dealerId, templateId) {
        try {
            //console.log("=== TEMPLATE ASSIGNMENT DEBUG ===");
            //console.log("Dealer ID:", dealerId, "Type:", typeof dealerId);
            //console.log("Template ID:", templateId, "Type:", typeof templateId);
            //console.log("Specialist ID:", this.props.typeId, "Type:", typeof this.props.typeId);
            
            // Validate inputs
            if (!dealerId) {
                throw new Error("Dealer ID is required");
            }
            if (!this.props.typeId) {
                throw new Error("Specialist ID is required");
            }
            
            const dealerIdInt = parseInt(dealerId);
            const specialistIdInt = parseInt(this.props.typeId);
            
            if (isNaN(dealerIdInt)) {
                throw new Error("Invalid dealer ID: " + dealerId);
            }
            if (isNaN(specialistIdInt)) {
                throw new Error("Invalid specialist ID: " + this.props.typeId);
            }
            
            if (templateId) {
                const templateIdInt = parseInt(templateId);
                if (isNaN(templateIdInt)) {
                    throw new Error("Invalid template ID: " + templateId);
                }
                
                // Assign template to dealership
                const assignRequest = "/templates/" + specialistIdInt + "/" + templateIdInt + "/dealerships";
                const assignmentData = { dealership_ids: [dealerIdInt] };
                
                //console.log("Assigning template via:", assignRequest);
                //console.log("Assignment data:", JSON.stringify(assignmentData, null, 2));

                await API.post(assignRequest, assignmentData);
                //console.log("Template assignment response:", response);
            } else {
                // Remove template assignment
                //console.log("Removing template assignment for dealer:", dealerIdInt);
                
                if (this.state.currentTemplateId) {
                    const currentTemplateIdInt = parseInt(this.state.currentTemplateId);
                    const removeRequest = "/templates/" + specialistIdInt + "/" + currentTemplateIdInt + "/dealerships";
                    const removalData = { dealership_ids: [dealerIdInt] };
                    
                    //console.log("Removing template assignment via:", removeRequest);
                    //console.log("Removal data:", JSON.stringify(removalData, null, 2));

                    await API.delete(removeRequest, { data: removalData });
                    //console.log("Template removal response:", response);
                } else {
                    //console.log("No current template to remove");
                }
            }

            this.setState({ currentTemplateId: templateId });
            //console.log("✅ Template assignment updated successfully");
            return true;
        } catch (error) {
            console.error("❌ Error updating template assignment:", error);
            console.error("Full error details:", error.response || error);
            
            if (error.response) {
                console.error("Response status:", error.response.status);
                console.error("Response data:", error.response.data);
            }
            
            if (error.response && error.response.status === 404) {
                alert("Template or dealership not found. Please check the configuration.");
            } else if (error.response && error.response.status === 500) {
                console.error("Server error details:", error.response.data);
                alert("Server error updating template assignment. Check server logs for details.\n\nError: " + (error.response.data?.message || error.message));
            } else if (error.response && error.response.status === 400) {
                console.error("Bad request details:", error.response.data);
                alert("Bad request: " + (error.response.data?.message || error.message));
            } else {
                alert("Error updating template assignment: " + error.message);
            }
            return false;
        }
    }

    // Toggle service detail expansion for a job row
    handleToggleJobExpand = async (requestId) => {
        const isExpanded = this.state.expandedJobRows.includes(requestId);
        if (isExpanded) {
            this.setState(prev => ({
                expandedJobRows: prev.expandedJobRows.filter(id => id !== requestId)
            }));
            return;
        }

        // Expand the row
        this.setState(prev => ({
            expandedJobRows: [...prev.expandedJobRows, requestId]
        }));

        // Fetch services if not already loaded
        if (!this.state.expandedJobServices[requestId]) {
            try {
                const { data: services } = await API.get(`/requestService/${requestId}`);
                this.setState(prev => ({
                    expandedJobServices: {
                        ...prev.expandedJobServices,
                        [requestId]: services || []
                    }
                }));
            } catch (err) {
                console.error('Failed to load services for request:', requestId, err);
                this.setState(prev => ({
                    expandedJobServices: {
                        ...prev.expandedJobServices,
                        [requestId]: []
                    }
                }));
            }
        }
    };

    // Navigate to walk-in form with job data pre-populated for rebooking
    handleRebookJob = (job) => {
        // Parse vehicle_description (e.g. "2024 Tesla Model 3") into year/make/model
        const vehicle = { vin: job.Vin || job.vin || job.VIN || job.vehicle_vin || '' };
        console.log("Rebook job data:", job);
        if (job.vehicle_description) {
            const parts = job.vehicle_description.trim().split(/\s+/);
            if (parts.length >= 3) {
                const yearCandidate = parseInt(parts[0], 10);
                if (yearCandidate >= 1950 && yearCandidate <= 2100) {
                    vehicle.year = parts[0];
                    vehicle.make = parts[1];
                    vehicle.model = parts.slice(2).join(' ');
                }
            }
        }
        const duplicateData = {
            customer: this.state.data.id,
            customerName: this.state.data.name,
            customerEmail: this.state.data.email,
            customerType: this.state.isDealerMode ? "whole" : "retail",
            vehicle: vehicle,
            requestId: job.Request_id
        };
        this.props.history.push('/newestimate', { duplicateData });
    };

    // Navigate to estimate form with customer pre-populated
    handleNewEstimate = () => {
        const duplicateData = {
            customer: this.state.data.id,
            customerName: this.state.data.name,
            customerEmail: this.state.data.email,
            customerType: "retail"
        };
        this.props.history.push('/newestimate', { duplicateData });
    };

    // Toggle between customer and dealer mode
    handleToggleMode = () => {
        //console.log("handleToggleMode called, current searchTerm:", this.state.searchTerm);
        // Clear form and results when switching modes
        this.setState(prevState => ({ 
            isDealerMode: !prevState.isDealerMode,
            found: " ",
            filteredResults: [],
            showDropdown: false,
            searchTerm: "", // Clear the search field
            selectedIndex: -1,
            data: {
                phone: "",
                email: "",
                name: "",
                type: "",
                id: "",
                stripe_id: "",
                billingEmail: "",
                dealerGroupId: null,
                dealerGroupName: null,
                dealerGroupCode: null
            },
            address: "",
            // Reset template selection
            selectedTemplateId: "",
            currentTemplateId: "",
            // Reset address components
            addressComponents: {
                line1: "",
                line2: "",
                city: "",
                state: "",
                zip: ""
            }
        }), () => {
            //console.log("After setState, searchTerm is:", this.state.searchTerm);
            // Force clear the input field and focus it
            if (this.searchInputRef.current) {
                this.searchInputRef.current.value = "";
                this.searchInputRef.current.focus();
            }
        });
    };

    // Enhanced search handler for custom dropdown
    handleSearchChange = (event) => {
        const value = event.target.value;
        this.setState({ 
            searchTerm: value,
            selectedIndex: -1 
        });

        if (!value.trim()) {
            this.setState({
                filteredResults: [],
                showDropdown: false
            });
            return;
        }

        // Use appropriate list based on mode
        const sourceList = this.state.isDealerMode ? this.state.dealerList : this.state.customerList;
        
        // Filter entities based on search term.
        // String(...) rather than entity.listValue.toLowerCase(): one row with a
        // missing listValue used to throw here, and a throw inside filter() means
        // NO results render at all — the search looked broken for every term,
        // not just for the malformed row.
        const needle = value.toLowerCase();
        const filtered = sourceList.filter(entity =>
            String((entity && entity.listValue) || '').toLowerCase().includes(needle)
        );

        this.setState({
            filteredResults: filtered,
            showDropdown: filtered.length > 0
        });
    };

    // Handle entity selection from dropdown
    handleEntitySelect = (entity) => {
        const idField = this.state.isDealerMode ? "dealer_id" : "customer_id";
        
        this.setState({
            searchTerm: entity.listValue,
            filteredResults: [],
            showDropdown: false,
            selectedIndex: -1
        });
        this.doSubmit(entity[idField]);
    };

    // Handle keyboard navigation
    handleKeyDown = (event) => {
        const { filteredResults, selectedIndex, showDropdown } = this.state;

        if (!showDropdown) {
            return;
        }

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                const nextIndex = selectedIndex < filteredResults.length - 1 ? selectedIndex + 1 : 0;
                this.setState({ selectedIndex: nextIndex });
                break;

            case 'ArrowUp':
                event.preventDefault();
                const prevIndex = selectedIndex > 0 ? selectedIndex - 1 : filteredResults.length - 1;
                this.setState({ selectedIndex: prevIndex });
                break;

            case 'Enter':
                event.preventDefault();
                if (selectedIndex >= 0 && filteredResults[selectedIndex]) {
                    this.handleEntitySelect(filteredResults[selectedIndex]);
                }
                break;

            case 'Escape':
                this.setState({
                    filteredResults: [],
                    showDropdown: false,
                    selectedIndex: -1
                });
                break;

            default:
                break;
        }
    };

    // Handle input focus
    handleFocus = () => {
        if (this.state.searchTerm && this.state.filteredResults.length > 0) {
            this.setState({ showDropdown: true });
        }
    };

    // Handle input blur with delay to allow for clicks
    handleBlur = () => {
        setTimeout(() => {
            this.setState({ 
                showDropdown: false,
                selectedIndex: -1
            });
        }, 150);
    };

    // Clear search
    clearSearch = () => {
        this.setState({ 
            searchTerm: "", 
            filteredResults: [],
            showDropdown: false,
            selectedIndex: -1,
            found: " "
        });
    };

    // Customer schema
    customerSchema = {
        phone: JOI.string().length(10).required().label('phone'),
        email: JOI.string().email().required().label('email'),
        name: JOI.string().required().label('name'),
        address: JOI.optional(),
        type: JOI.optional(),
        id: JOI.optional(),
        stripe_id: JOI.optional(),
        billingEmail: JOI.optional()
    }; 

    // Dealer schema - only email is required since other fields are read-only
    dealerSchema = {
        phone: JOI.optional(),
        email: JOI.string().email().required().label('email'),
        name: JOI.optional(),
        address: JOI.optional(),
        type: JOI.optional(),
        id: JOI.optional(),
        stripe_id: JOI.optional(),
        billingEmail: JOI.string().email().allow('').optional().label('billing email'),
        latitude: JOI.optional(),
        longitude: JOI.optional(),
        stripeInvoicePrefix: JOI.optional(),
        dealerGroupId: JOI.optional(),
        dealerGroupName: JOI.optional(),
        dealerGroupCode: JOI.optional(),
        assetType: JOI.optional()
    };

    // Get current schema based on mode
    get schema() {
        return this.state.isDealerMode ? this.dealerSchema : this.customerSchema;
    }

    // Get the full customer details for the selected customer or dealer
    async getEntity(id) {
        if (this.state.isDealerMode) {
            await this.getDealer(id);
        } else {
            await this.getCustomer(id);
        }
    }

    // Get customer details
    async getCustomer(custId) {            
        const customerRequest = "/customers/" + custId;
        const { data: customer } = await API.get(customerRequest);
        //console.log("GetCustomer: customers: ", customer);
        let cust = customer[0];            
        if (customer.length > 0) {
            let data = {
                name: cust.customer_name,
                phone: cust.customer_phone_number,
                email: cust.customer_Email_Address,
                type: cust.customer_type,
                id: cust.customer_id,
                stripe_id: cust.stripe_customer_id,
                billingEmail: "" // Empty for regular customers
            }
            this.setState({ found: "Y", data, address: cust.customer_address });
            
            // Get the customers previous jobs at the specialist
            const custJobs = "/getcustomerjobs/" + cust.customer_id + "/" + this.props.typeId;
            const { data: customerJobs } = await API.get(custJobs);
            this.setState({ customerJobs, expandedJobRows: [], expandedJobServices: {} });
        } else {
            this.setState({ found: "N" });
        }
    };

    // Get dealer details
    async getDealer(dealerId) {            
        const dealerRequest = "/dealers/" + dealerId;
        const { data: dealer } = await API.get(dealerRequest);
        //console.log("GetDealer: dealers: ", dealer);
        let dlr = dealer[0];            
        if (dealer.length > 0) {
            let data = {
                name: dlr.dealer_name,
                phone: dlr.dealer_phone_number,
                email: dlr.dealer_email,
                type: dlr.dealer_type,
                id: dlr.dealer_id,
                stripe_id: dlr.stripe_customer_id,
                billingEmail: dlr.billing_email || dlr.dealer_email,
                // Store current lat/long values
                latitude: dlr.latitude,
                longitude: dlr.longitude,
                // Preserve invoice prefix field
                stripeInvoicePrefix: dlr.stripe_invoice_prefix,
                // Dealer group fields.
                // `?? null`, NOT `|| null` — the "Independent" group's id is 0 in
                // production, and `||` would rewrite it to null. Once the group becomes
                // editable here and PUT /dealers/:id starts carrying dealerGroupId, that
                // would silently un-group every Independent dealership on save. Cannot
                // reproduce on dev, where Independent is 2.
                dealerGroupId: dlr.dealer_group_id ?? null,
                dealerGroupName: dlr.dealer_group_name || null,
                dealerGroupCode: dlr.dealer_group_code || null,
                // Active/inactive flag (server returns is_active; absent ⇒ active)
                is_active: dlr.is_active,
                // ENUM('auto','marine') — a STRING, not a tinyint. RAW, not
                // defaulted — 'absent' must stay distinguishable from 'auto', or an
                // unrelated edit will write a value we never read.
                assetType: dlr.asset_type
            }
            
            // Store separate address components for updating
            const addressComponents = {
                line1: dlr.dealer_address_line1 || dlr.dealer_Address_Line1 || "",
                line2: dlr.dealer_address_line2 || dlr.dealer_Address_Line2 || "",
                city: dlr.dealer_address_city || dlr.dealer_Address_City || "",
                state: dlr.dealer_address_state || dlr.dealer_Address_State || "",
                zip: dlr.dealer_address_zip || dlr.dealer_Address_Zip || ""
            };
            
            // Format address for display
            const formattedAddress = this.formatAddress(
                addressComponents.line1, 
                addressComponents.city, 
                addressComponents.state, 
                addressComponents.zip
            );
            
            this.setState({ 
                found: "Y", 
                data, 
                address: formattedAddress,
                addressComponents: addressComponents
            });
            
            // Make sure templates are loaded first, then load template assignment
            if (this.state.templates.length === 0) {
                //console.log("Templates not loaded yet, loading now...");
                await this.loadTemplates();
            }
            
            // Load current template assignment for this dealership
            await this.loadDealershipTemplate(dlr.dealer_id);
            
            // Get the dealers previous jobs at the specialist
            const dealerJobs = "/getdealerjobs/" + dlr.dealer_id + "/" + this.props.typeId;
            const { data: customerJobs } = await API.get(dealerJobs);
            this.setState({ customerJobs, expandedJobRows: [], expandedJobServices: {} });
        } else {
            this.setState({ found: "N" });
        }
    };

    // Handle template selection change
    handleTemplateChange = (event) => {
        this.setState({ selectedTemplateId: event.target.value });
    };

    // Update the record with the updated information
    async updateEntity() {
        if (this.state.isDealerMode) {
            // For dealers, don't pass the old combined parameters
            await this.updateDealer();
        } else {
            await this.updateCustomer(
                this.state.data.name, 
                this.state.data.email, 
                this.state.data.phone, 
                this.state.address
            );
        }
    }

    // Update customer record
    async updateCustomer(name, email, phone, address) {            
        //console.log('Updating Customer Record');
        const customer = {
            customerName: name,
            customerEmailAddress: email,
            customerPhoneNumber: phone,
            customerAddress: address,
            customerType: this.state.data.type,
            customerId: this.state.data.id,
            stripeCustomerId: this.state.data.stripe_id
        };
        //console.log('Record:', customer);
      
        const apiEndpoint = "/customer/" + customer.customerId;
        await API.put(apiEndpoint, customer);
        this.resetForm();
        alert("Customer record updated");
        window.location = "/customers";
    };

    // Update dealer record - includes template assignment if templates are available
    async updateDealer() {            
        //console.log('Updating Dealer Record');
        //console.log('Address components:', this.state.addressComponents);
        //console.log('Combined address:', this.state.address);
        
        // If address components are empty, try to use the combined address as fallback
        let addressComponents = this.state.addressComponents;
        if (!addressComponents.line1 && this.state.address) {
            //console.log('Address components missing, using combined address as line1');
            addressComponents = {
                line1: this.state.address,
                line2: '',
                city: '',
                state: '',
                zip: ''
            };
        }
        
        const dealer = {
            // Match the EXACT field names expected by your dealer model constructor
            dealerId: this.state.data.id,
            dealerName: this.state.data.name,
            dealerEmail: this.state.data.email,
            billingEmail: this.state.data.billingEmail,
            dealerPhoneNumber: this.state.data.phone,
            stripeCustomerId: this.state.data.stripe_id,  // Changed from stripeDealerId
            // Send the separate address components (not the combined address string)
            dealerAddressLine1: addressComponents.line1 || '',
            dealerAddressLine2: addressComponents.line2 || '',
            dealerAddressCity: addressComponents.city || '',
            dealerAddressState: addressComponents.state || '',
            dealerAddressZip: addressComponents.zip || '',
            // Use existing lat/long values from database, or null if empty
            latitude: this.state.data.latitude || null,
            longitude: this.state.data.longitude || null,
            // Preserve invoice prefix field
            stripeInvoicePrefix: this.state.data.stripeInvoicePrefix || null
        };
        // Only send a type we actually read, or one the user actively picked.
        // Sending a defaulted 'auto' would downgrade a marine dealership on any
        // unrelated edit if the server's read projection lags its write path.
        if (this.state.data.assetType !== undefined && this.state.data.assetType !== null) {
            dealer.assetType = this.state.data.assetType;
        }
        //console.log('Record being sent to server:', dealer);
      
        try {
            // Try the plural endpoint first (most likely correct)
            let apiEndpoint = "/dealers/" + dealer.dealerId;
            //console.log('Attempting to update dealer at endpoint:', apiEndpoint);
            
            try {
                await API.put(apiEndpoint, dealer);
                //console.log('Dealer update successful:', dealerUpdateResult);
            } catch (error) {
                // If plural fails, try singular as fallback
                if (error.response && error.response.status === 404) {
                    //console.log('Plural endpoint failed, trying singular...');
                    apiEndpoint = "/dealer/" + dealer.dealerId;
                    await API.put(apiEndpoint, dealer);
                    //console.log('Dealer update successful with singular endpoint:', dealerUpdateResult);
                } else {
                    throw error; // Re-throw if it's not a 404
                }
            }
            
            // Update template assignment if it has changed AND templates are available
            let templateUpdateMessage = "";
            if (this.state.templates.length > 0 && this.state.selectedTemplateId !== this.state.currentTemplateId) {
                //console.log("Template selection changed, attempting to update...");
                
                const templateUpdateSuccess = await this.updateDealershipTemplate(
                    dealer.dealerId, 
                    this.state.selectedTemplateId
                );
                
                if (templateUpdateSuccess) {
                    // Update the current template ID to reflect the change
                    this.setState({ currentTemplateId: this.state.selectedTemplateId });
                    templateUpdateMessage = " Template assignment updated.";
                } else {
                    // Don't stop the whole process if template update fails
                    //console.log("Template update failed, but dealer info was updated successfully");
                    templateUpdateMessage = " Template update failed.";
                }
            }
            
            // Show success message but DON'T redirect - keep the record displayed
            const successMessage = this.state.templates.length > 0 && this.state.selectedTemplateId !== this.state.currentTemplateId ? 
                `Dealer record updated successfully.${templateUpdateMessage}` : 
                "Dealer record updated successfully.";
            
            alert(successMessage);
            
            // Optionally refresh the dealer data to show any backend changes (like geocoded coordinates)
            await this.getDealer(dealer.dealerId);
            
        } catch (error) {
            console.error('Error updating dealer:', error);
            console.error('Error response:', error.response);
            
            if (error.response) {
                if (error.response.status === 404) {
                    alert("Dealer update endpoint not found. Please check that the /dealer/:dealerId route exists in your server.");
                } else {
                    alert(`Error updating dealer: ${error.response.status} - ${error.response.data?.message || error.message}`);
                }
            } else {
                alert(`Error updating dealer: ${error.message}`);
            }
        }
    };

    // Reset form after update or cancel - preserve isDealerMode
    resetForm() {
        let data = {
            name: "",
            phone: "",
            email: "",
            type: "",
            id: "",
            stripe_id: "",
            billingEmail: "",
            dealerGroupId: null,
            dealerGroupName: null,
            dealerGroupCode: null
        }
        this.setState({ 
            data, 
            found: "N", 
            searchTerm: "", 
            address: "",
            filteredResults: [],
            showDropdown: false,
            selectedIndex: -1,
            selectedTemplateId: "",
            currentTemplateId: "",
            addressComponents: {
                line1: "",
                line2: "",
                city: "",
                state: "",
                zip: ""
            }
        }, () => {
            // Force clear the input field after state update
            if (this.searchInputRef.current) {
                this.searchInputRef.current.value = "";
                this.searchInputRef.current.focus();
            }
        });
    }

    // If cancel clicked, return back to the unselected list - preserve mode selection
    handleCancel = () => {
        this.resetForm();
        // Don't redirect - just reset the form
    };

    // Validate the data entered for the record
    validate = () => {
        const options = { abortEarly: false };
        const { error } = JOI.validate(this.state.data, this.schema, options);
        //console.log("Errors:", error);
        
        // Also validate address separately since it's in a different state property
        if (!error && this.state.isDealerMode && !this.state.address) {
            const errors = { address: "Address is required" };
            this.setState({ errors });
            return false;
        }
        
        if (!error) return true;
    
        const errors = {};
        for (let item of error.details) errors[item.path[0]] = item.message;

        this.setState({ errors });
   
        return false;
    };

    // Validate the field entered based on the schema
    validateProperty = ({ name, value }) => {
        const obj = { [name]: value };
        const schema = { [name]: this.schema[name] };
        const { error } = JOI.validate(obj, schema);
        return error ? error.details[0].message : null;
    };
      
    handleDataChange = ({ currentTarget: input }) => {
        const errors = { ...this.state.errors };
        const errorMessage = this.validateProperty(input);
        if (errorMessage) errors[input.name] = errorMessage;
        else delete errors[input.name];
    
        const data = { ...this.state.data };
        data[input.name] = input.value;
    
        this.setState({ data, errors });
    };

    handleAddress = (place) => {
        const address = place.formatted_address;
        this.setState({ address });

        // Clear address error if it exists
        if (this.state.errors && this.state.errors.address) {
            const errors = {...this.state.errors};
            delete errors.address;
            this.setState({ errors });
        }
    }
    
    // Go select the information for the selected entity      
    doSubmit = (id) => {
        // Get the customer or dealer information from db 
        this.getEntity(id);
    };
            
    doUpdate = () => {
        // Update the customer or dealer information in db 
        if (this.validate()) {
            var updatePromise = this.updateEntity();
            // When the promise is resolved, update the state variables
            updatePromise.then(done => {
                this.setState({ found: "Y" });
            });
        } else {
            //console.log("Error validating:");
        }
    };
    
    render() {
        const isViewer = isViewerRole(this.props.userRole);

        return (
            <React.Fragment>
                {/* Mode Toggle Buttons */}
                <div className="text-center mb-3">
                    <div className="btn-group" role="group">
                        <button 
                            type="button"
                            className={`btn ${!this.state.isDealerMode ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => {
                                if (this.state.isDealerMode) {
                                    this.handleToggleMode();
                                }
                            }}>
                            Retail
                        </button>
                        <button
                            type="button"
                            className={`btn ${this.state.isDealerMode ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => {
                                if (!this.state.isDealerMode) {
                                    this.handleToggleMode();
                                }
                            }}>
                            Dealer
                        </button>
                    </div>
                </div>

                <form className="bg-complete shadow p-5 corners">
                    <div className="form-row justify-content-center">
                        <div className="form-group col-12 col-md-2 text-center">
                            <label htmlFor="name" className="bold mt-1">
                                Enter {this.state.isDealerMode ? "Dealership Name" : "Name or Number"}
                            </label>
                        </div>
                        <div className="search-bar-container col-12 col-md-6" style={{ position: 'relative' }}>
                            <div className="input-group">
                                <input
                                    ref={this.searchInputRef}
                                    type="text"
                                    className="form-control"
                                    placeholder={`Type to search for ${this.state.isDealerMode ? "dealerships" : "customers"}...`}
                                    autoFocus
                                    value={this.state.searchTerm}
                                    onChange={this.handleSearchChange}
                                    onKeyDown={this.handleKeyDown}
                                    onFocus={this.handleFocus}
                                    onBlur={this.handleBlur}
                                    autoComplete="off"
                                />
                                {this.state.searchTerm && (
                                    <div className="input-group-append">
                                        <button 
                                            className="btn btn-outline-secondary"
                                            onClick={this.clearSearch}
                                            type="button"
                                            title="Clear search"
                                        >
                                            <i className="fa-sharp fa-regular fa-times"></i>
                                        </button>
                                    </div>
                                )}
                            </div>
                            
                            {/* Custom dropdown */}
                            {this.state.showDropdown && (
                                <div 
                                    style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        backgroundColor: 'white',
                                        border: '1px solid #ccc',
                                        borderTop: 'none',
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                        zIndex: 1000,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    {this.state.filteredResults.map((entity, index) => {
                                        const idField = this.state.isDealerMode ? "dealer_id" : "customer_id";
                                        return (
                                            <div
                                                key={entity[idField]}
                                                style={{
                                                    padding: '8px 12px',
                                                    cursor: 'pointer',
                                                    backgroundColor: index === this.state.selectedIndex ? '#e9ecef' : 'white',
                                                    borderBottom: index < this.state.filteredResults.length - 1 ? '1px solid #eee' : 'none'
                                                }}
                                                onMouseDown={(e) => {
                                                    e.preventDefault(); // Prevent blur from firing
                                                    this.handleEntitySelect(entity);
                                                }}
                                                onMouseEnter={() => this.setState({ selectedIndex: index })}
                                            >
                                                {entity.listValue}
                                                {this.state.isDealerMode && this.isDealerInactive(entity) && this.renderInactiveBadge()}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>  
                    </div>
                </form>
                
                {this.state.found === "Y" && (
                    <div className="jv-card mt-3">
                        <div className="jv-card-header">
                            <h5 className="jv-card-title">
                                {this.state.isDealerMode ? "Dealership Information" : (isViewer ? "Customer Information" : "Customer Information")}
                            </h5>
                        </div>

                        {/* Retail Customer Form */}
                        {!this.state.isDealerMode && (
                            <>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="jv-form-group">
                                            <label className="jv-label jv-label-required">Full Name</label>
                                            <input
                                                className="jv-input"
                                                type="text"
                                                name="name"
                                                value={this.state.data.name}
                                                onChange={this.handleDataChange}
                                                readOnly={isViewer}
                                            />
                                            {this.state.errors['name'] &&
                                                <div className="text-danger mt-1" style={{ fontSize: 'var(--font-size-sm)' }}>{this.state.errors['name']}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="jv-form-group">
                                            <label className="jv-label jv-label-required">Phone</label>
                                            <input
                                                className="jv-input"
                                                type="text"
                                                name="phone"
                                                value={this.state.data.phone}
                                                onChange={this.handleDataChange}
                                                readOnly={isViewer}
                                            />
                                            {this.state.errors['phone'] &&
                                                <div className="text-danger mt-1" style={{ fontSize: 'var(--font-size-sm)' }}>{this.state.errors['phone']}</div>}
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="jv-form-group">
                                            <label className="jv-label jv-label-required">Email</label>
                                            <input
                                                className="jv-input"
                                                type="text"
                                                name="email"
                                                value={this.state.data.email}
                                                onChange={this.handleDataChange}
                                                readOnly={isViewer}
                                            />
                                            {this.state.errors['email'] &&
                                                <div className="text-danger mt-1" style={{ fontSize: 'var(--font-size-sm)' }}>{this.state.errors['email']}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="jv-form-group">
                                            <label className="jv-label">Address</label>
                                            {isViewer ? (
                                                <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--color-text-primary)' }}>{this.state.address || '—'}</div>
                                            ) : (
                                                <Autocomplete
                                                    className="jv-input"
                                                    apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                                                    onPlaceSelected={(place) => this.handleAddress(place)}
                                                    options={{
                                                        types: [],
                                                        componentRestrictions: { country: "us" },
                                                    }}
                                                    defaultValue={this.state.address}
                                                />
                                            )}
                                            {this.state.errors['address'] &&
                                                <div className="text-danger mt-1" style={{ fontSize: 'var(--font-size-sm)' }}>{this.state.errors['address']}</div>}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Dealer Form */}
                        {this.state.isDealerMode && (
                            <>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="jv-form-group">
                                            <label className="jv-label">Dealership Name</label>
                                            <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-primary)' }}>
                                                {this.state.data.name}
                                                {this.isDealerInactive(this.state.data) && this.renderInactiveBadge()}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="jv-form-group">
                                            <label className="jv-label">Phone</label>
                                            <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--color-text-primary)' }}>{this.state.data.phone}</div>
                                        </div>
                                    </div>
                                </div>
                                {this.state.data.dealerGroupName && (
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="jv-form-group">
                                                <label className="jv-label">Dealer Group</label>
                                                <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--color-text-secondary)' }}>{this.state.data.dealerGroupName}</div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="jv-form-group">
                                            <label className="jv-label jv-label-required">Email</label>
                                            <input
                                                className="jv-input"
                                                type="text"
                                                name="email"
                                                value={this.state.data.email}
                                                onChange={this.handleDataChange}
                                                readOnly={isViewer}
                                            />
                                            {this.state.errors['email'] &&
                                                <div className="text-danger mt-1" style={{ fontSize: 'var(--font-size-sm)' }}>{this.state.errors['email']}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="jv-form-group">
                                            <label className="jv-label">Billing Email</label>
                                            <input
                                                className="jv-input"
                                                type="text"
                                                name="billingEmail"
                                                value={this.state.data.billingEmail}
                                                onChange={this.handleDataChange}
                                                readOnly={isViewer}
                                            />
                                            {this.state.errors['billingEmail'] &&
                                                <div className="text-danger mt-1" style={{ fontSize: 'var(--font-size-sm)' }}>{this.state.errors['billingEmail']}</div>}
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="jv-form-group">
                                            <label className="jv-label">Address</label>
                                            <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--color-text-primary)' }}>{this.state.address || '—'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="jv-form-group">
                                            <label className="jv-label">Catalog Template</label>
                                            <select
                                                className="jv-input"
                                                name="template"
                                                value={this.state.selectedTemplateId}
                                                onChange={this.handleTemplateChange}
                                                disabled={isViewer || this.visibleTemplates().length === 0}
                                            >
                                                {this.visibleTemplates().length === 0 ? (
                                                    <option value="">No templates available</option>
                                                ) : (
                                                    <>
                                                        <option value="">No Template Assigned</option>
                                                        {this.visibleTemplates().map(template => (
                                                            <option key={template.template_id} value={template.template_id}>
                                                                {template.template_name}
                                                                {template.service_count ? ` (${template.service_count} services)` : ''}
                                                            </option>
                                                        ))}
                                                    </>
                                                )}
                                            </select>
                                            {this.state.currentTemplateId && (
                                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-success)', marginTop: 'var(--space-1)' }}>
                                                    Currently assigned: {(this.state.templates || []).find(t => t.template_id === this.state.currentTemplateId)?.template_name || 'Template ' + this.state.currentTemplateId}
                                                </div>
                                            )}
                                            {!this.state.currentTemplateId && this.state.templates.length > 0 && (
                                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-1)' }}>
                                                    No template currently assigned
                                                </div>
                                            )}

                                            {this.state.selectedTemplateId !== this.state.currentTemplateId && this.state.selectedTemplateId && (
                                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-warning)', marginTop: 'var(--space-1)' }}>
                                                    Will assign template: {this.state.templates.find(t => t.template_id === this.state.selectedTemplateId)?.template_name}
                                                </div>
                                            )}
                                            {this.state.selectedTemplateId !== this.state.currentTemplateId && !this.state.selectedTemplateId && this.state.currentTemplateId && (
                                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', marginTop: 'var(--space-1)' }}>
                                                    Will remove current template assignment
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="jv-form-group">
                                            <label className="jv-label">Dealership Type</label>
                                            <select
                                                className="jv-input"
                                                name="assetType"
                                                value={this.state.data.assetType || 'auto'}
                                                onChange={this.handleDataChange}
                                                disabled={isViewer}
                                            >
                                                <option value="auto">Automotive</option>
                                                <option value="marine">Marine</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Action Buttons */}
                        <div className="jv-action-bar" style={{ justifyContent: 'center' }}>
                            {!isViewer && !this.state.isDealerMode && (
                                <button type="button" className="jv-btn jv-btn-primary" onClick={this.doUpdate}>
                                    Update Customer
                                </button>
                            )}
                            {!isViewer && this.state.isDealerMode && (
                                <button type="button" className="jv-btn jv-btn-primary" onClick={this.doUpdate}>
                                    {this.state.selectedTemplateId !== this.state.currentTemplateId ?
                                        'Update Dealer & Template' : 'Update Dealer'}
                                </button>
                            )}
                            <button type="button" className="jv-btn jv-btn-tertiary" onClick={this.handleCancel}>
                                Cancel
                            </button>
                            {!isViewer && !this.state.isDealerMode && (
                                <button type="button" className="jv-btn jv-btn-secondary" onClick={this.handleNewEstimate}>
                                    <i className="fa-sharp fa-regular fa-plus" /> New Estimate
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Jobs Table - Show below the details when entity is selected */}
                {this.state.found === "Y" && (() => {
                    // Filter jobs to only show scheduled, started, received, finished, reviewed, or collected
                    const filteredJobs = this.state.customerJobs
                        .filter(job =>
                            job.current_state === 'scheduled' ||
                            job.current_state === 'started' ||
                            job.current_state === 'received' ||
                            job.current_state === 'finished' ||
                            job.current_state === 'reviewed' ||
                            job.current_state === 'collected'
                        )
                        .sort((a, b) => {
                            // Sort by date descending (most recent first)
                            const dateA = new Date(a.created_date || 0);
                            const dateB = new Date(b.created_date || 0);
                            return dateB - dateA;
                        });

                    if (filteredJobs.length === 0) return null;

                    return (
                        <div className="m-2 mt-4 shadow p-3" style={{ backgroundColor: "#FFFFFF" }}>
                            <div className="row">
                                <div className="col text-center">
                                    <h5>Previous Jobs</h5>
                                </div>
                            </div>
                            <div className="row mt-3">
                                <div className="col">
                                    <table className="table table-striped table-hover">
                                        <thead className="thead-dark">
                                            <tr>
                                                <th style={{ width: '40px' }}></th>
                                                <th>Date</th>
                                                <th>Service</th>
                                                <th>Vehicle</th>
                                                <th>Status</th>
                                                <th>Total</th>
                                                {!isViewer && <th>Action</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredJobs.map((job) => {
                                                // Calculate final amount with discount
                                                const requestTotal = parseFloat(job.Request_Total || 0);
                                                const requestDiscount = parseFloat(job.Request_Discount || 0);
                                                const finalAmount = requestTotal - requestDiscount;

                                                // Format the date
                                                const formattedDate = job.created_date
                                                    ? convertFromUTC(job.created_date).format('MMM D, YYYY')
                                                    : 'N/A';

                                                // Determine status display
                                                const statusBadge = {
                                                    'scheduled': { color: 'info', label: 'Scheduled' },
                                                    'started': { color: 'warning', label: 'In Progress' },
                                                    'received': { color: 'primary', label: 'Received' },
                                                    'finished': { color: 'success', label: 'Finished' },
                                                    'reviewed': { color: 'success', label: 'Reviewed' },
                                                    'collected': { color: 'success', label: 'Collected' }
                                                }[job.current_state] || { color: 'secondary', label: job.current_state || 'N/A' };

                                                const isExpanded = this.state.expandedJobRows.includes(job.Request_id);
                                                const services = this.state.expandedJobServices[job.Request_id];
                                                const colSpan = !isViewer ? 7 : 6;

                                                return (
                                                    <React.Fragment key={job.Request_id}>
                                                        <tr>
                                                            <td
                                                                style={{ cursor: 'pointer', color: '#6b7280', userSelect: 'none' }}
                                                                onClick={() => this.handleToggleJobExpand(job.Request_id)}
                                                            >
                                                                <i className={`fa-sharp fa-regular ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
                                                            </td>
                                                            <td>{formattedDate}</td>
                                                            <td>{job.Service_Requested || 'N/A'}</td>
                                                            <td>
                                                                {/* Boats get a marker; road cars get none, so the
                                                                    icon reads at a glance down a mixed list. */}
                                                                {isMarineVehicle(job) && (
                                                                    <i
                                                                        className="fa-sharp fa-regular fa-sailboat mr-1"
                                                                        title="Boat"
                                                                    ></i>
                                                                )}
                                                                {job.vehicle_description || 'N/A'}
                                                            </td>
                                                            <td>
                                                                <span className={`badge badge-${statusBadge.color}`}>
                                                                    {statusBadge.label}
                                                                </span>
                                                            </td>
                                                            <td>${finalAmount.toFixed(2)}</td>
                                                            {!isViewer && (
                                                            <td>
                                                                {['finished', 'reviewed', 'collected'].includes(job.current_state) && (
                                                                    <button
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={() => this.handleRebookJob(job)}
                                                                        title="Rebook this job"
                                                                    >
                                                                        <i className="fa-sharp fa-regular fa-redo mr-1"></i>Rebook
                                                                    </button>
                                                                )}
                                                            </td>
                                                            )}
                                                        </tr>
                                                        {isExpanded && (
                                                            <tr>
                                                                <td colSpan={colSpan} style={{ padding: 0 }}>
                                                                    <div style={{ background: '#f8fafc', padding: '16px 20px', borderLeft: '4px solid #3FB2BE' }}>
                                                                        {!services ? (
                                                                            <p className="text-muted mb-0">
                                                                                <i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>Loading services...
                                                                            </p>
                                                                        ) : services.length === 0 ? (
                                                                            <p className="text-muted mb-0">No service details available</p>
                                                                        ) : (
                                                                            <>
                                                                                <strong style={{ fontSize: '14px', color: '#374151' }}>Services ({services.length})</strong>
                                                                                <table className="table table-sm mt-2 mb-0" style={{ background: 'transparent' }}>
                                                                                    <thead>
                                                                                        <tr>
                                                                                            <th>Service</th>
                                                                                            <th>Amount</th>
                                                                                            <th>Status</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {services.map((svc, idx) => {
                                                                                            const description = svc.custom_description || svc.Description || svc.service_name || svc.Service_Name || '-';
                                                                                            const catPrefix = svc.service_category ? `${svc.service_category} - ` : '';
                                                                                            // `service_cost` on a refetched line is the CATALOG
                                                                                            // rate — a per-unit figure that ignores quantity
                                                                                            // entirely ($8.50/sq ft on a 147 sq ft job). The
                                                                                            // line's own total is service_amount; fall back to
                                                                                            // unit x quantity, and only then to the rate.
                                                                                            const qty = parseInt(svc.quantity ?? svc.service_quantity ?? svc.serviceQuantity, 10) || 1;
                                                                                            const unitPrice = parseFloat(svc.unit_price ?? svc.unitPrice ?? 0) || 0;
                                                                                            const stored = parseFloat(svc.service_amount ?? svc.service_value ?? 0) || 0;
                                                                                            const cost = stored || (unitPrice * qty) || parseFloat(svc.service_cost || 0) || 0;
                                                                                            const discount = parseFloat(svc.service_discount || 0);
                                                                                            const amount = Math.max(0, cost - discount);
                                                                                            const state = svc.service_state || svc.Service_State || 'N/A';
                                                                                            // Show the working, so a big number is explicable.
                                                                                            const breakdown = isAreaPriced(svc)
                                                                                                ? formatMarineLine(svc)
                                                                                                : (qty > 1 && unitPrice > 0 ? `x${qty} @ $${unitPrice.toFixed(2)}` : '');
                                                                                            return (
                                                                                                <tr key={idx}>
                                                                                                    <td>
                                                                                                        {catPrefix}{description}
                                                                                                        {breakdown && (
                                                                                                            <span className="ml-1 text-muted small">{breakdown}</span>
                                                                                                        )}
                                                                                                    </td>
                                                                                                    <td>${amount.toFixed(2)}</td>
                                                                                                    <td>
                                                                                                        <span className={`badge badge-${
                                                                                                            state === 'finished' ? 'success' :
                                                                                                            state === 'started' ? 'warning' : 'secondary'
                                                                                                        }`}>
                                                                                                            {state}
                                                                                                        </span>
                                                                                                    </td>
                                                                                                </tr>
                                                                                            );
                                                                                        })}
                                                                                    </tbody>
                                                                                </table>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </React.Fragment>
        );
    }
}